import type {
  ColumnDef,
  ColumnFilter,
  ExpandedState,
  OnChangeFn,
  Row,
  RowData,
  RowPinningState,
  SortingState,
  Table as TableType,
} from "@tanstack/react-table";
import {
  type PropsWithChildren,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import type { ZodType, ZodTypeDef } from "zod";

import { TableLayout } from "@/components/layouts/table-layout";
import { ErrorMessage } from "@/components/ui/error";
import { VirtualizedTable } from "@/components/ui/virtualized-table";
import type { ServerFilters } from "@/hooks/useQueryFilters";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";

import { useSpinDelay } from "spin-delay";
import { DataTablePagination } from "../tables/DataTablePagination";
import { SkeletonTable } from "../tables/skeleton-table";
import { NoRecordsEmpty } from "./empty-message";
import FiltersRoot from "./filters";
import { useTable } from "./useTable";

// Base table props that are common to both context and non-context versions
export type BaseTableProps<T = unknown> = {
  loading?: boolean;
  onRowClick?: (event: React.MouseEvent, row: any, index: number) => void;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  autoSize?: boolean;
  rowValidationSchema?: ZodType<any, ZodTypeDef, any>;
  children?: (props: any) => React.ReactNode;
  estimateSize?: number;
  emptyStateComponent?: React.ReactNode;
  table: TableType<T>;
  clickableColumns: string[];
  columnVisibility: Record<string, boolean>;
  columns: ColumnDef<T>[];
  data: T[];
  stickyColumns?: {
    left?: string[];
    right?: string[];
  };
  footer?: React.ReactNode | false;
  error?: Error | string;
  hideHeader?: boolean;
  getSubRowStyle?: (row: Row<T>) => string | boolean;
  bordered?: boolean;
  rowClassName?: React.HTMLAttributes<HTMLTableRowElement>["className"];
};

// The base table component that ca
// n be used with or without context
function _BaseTable<T = unknown>(
  {
    loading = false,
    onRowClick,
    className,
    autoSize = true,
    rowValidationSchema,
    children,
    estimateSize,
    emptyStateComponent,
    table,
    clickableColumns,
    columnVisibility,
    columns,
    data,
    stickyColumns,
    footer,
    error,
    hideHeader = false,
    getSubRowStyle,
    bordered = false,
    rowClassName,
  }: BaseTableProps<T>,
  ref: React.ForwardedRef<HTMLTableElement>,
) {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const getEmptyStateComponent = (table: TableType<T>) => {
    const hasActiveFilters = table.hasActiveServerSideFilters?.() ?? false;

    if (hasActiveFilters) {
      return (
        <NoRecordsEmpty
          state="no-filter-results"
          onReset={table.clearServerSideFilters}
        />
      );
    }

    if (emptyStateComponent) {
      return emptyStateComponent;
    }

    return <NoRecordsEmpty state="no-records" />;
  };

  const isLoading = useSpinDelay(loading);

  if (error) {
    const errorMessage = error instanceof Error ? error.message : error;

    const retry = () => {
      if (typeof window !== "undefined") {
        const { origin, pathname } = window.location;
        window.location.replace(`${origin}${pathname}`);
      }
    };

    return (
      <div className="flex flex-1 rounded-md border overflow-hidden">
        <ErrorMessage message={errorMessage} retry={retry} />
      </div>
    );
  }

  return (
    <TableLayout
      ref={tableContainerRef}
      className={className}
      autoSize={autoSize}
    >
      {isLoading || !data ? (
        <SkeletonTable
          columns={columns}
          columnVisibility={columnVisibility}
          tableContainerRef={tableContainerRef}
          hideHeader={hideHeader}
        />
      ) : (
        <VirtualizedTable<T>
          ref={ref}
          tableContainerRef={tableContainerRef}
          table={table}
          loading={loading}
          clickableColumns={clickableColumns}
          onRowClick={onRowClick}
          rowValidationSchema={rowValidationSchema}
          estimateSize={estimateSize}
          stickyColumns={stickyColumns}
          footer={footer}
          hideHeader={hideHeader}
          getSubRowStyle={getSubRowStyle}
          bordered={bordered}
          rowClassName={rowClassName}
          getEmptyStateComponent={getEmptyStateComponent}
        >
          {children && ((props) => children(props))}
        </VirtualizedTable>
      )}
    </TableLayout>
  );
}

export const BaseTable = forwardRef(_BaseTable) as <T = unknown>(
  props: BaseTableProps<T> & { ref?: React.ForwardedRef<HTMLTableElement> },
) => React.ReactElement;

// Context type definition
type TableContextType<T = any> = {
  data: T[];
  table: TableType<T>;
  tableName: string;
  globalFilter: string;
  columnFilters: ColumnFilter[];
  clickableColumns: string[];
  focusedRowId: string | undefined;
  columnVisibility: Record<string, boolean>;
  loadingRows: Record<string, boolean>;
  columns: ColumnDef<T>[];
  setRowSelection: (value: any) => void;
  setRowPinning: (value: RowPinningState) => void;
  onGlobalFilterChange: (value: string) => void;
  onColumnFilterChange: (filterState: ColumnFilter[]) => void;
  setColumnFilters: (filters: ColumnFilter[]) => void;
  meta: Record<string, any>;
  rowSelection: Record<string, boolean>;
  rowPinning: RowPinningState;
  stickyColumns?: {
    left?: string[];
    right?: string[];
  };
  footer?: React.ReactNode | false;
};

const TableInstanceContext = createContext<TableContextType | undefined>(
  undefined,
);

export function useTableInstanceContext<T = any>() {
  const context = useContext<TableContextType<T> | undefined>(
    TableInstanceContext,
  );
  if (context === undefined) {
    throw new Error(
      "useTableInstanceContext must be within TableInstanceProvider",
    );
  }
  return context;
}

// Props for the context provider
type TableInstanceProviderProps<T> = PropsWithChildren<{
  tableName: string;
  data: T[];
  columns: ColumnDef<T>[];
  clickableColumns?: string[];
  initialColumnVisibility?: Record<string, boolean>;
  getRowId?: (row: T) => string;
  onRowActions?: Record<string, (rowId: string) => void>;
  getExpandedRowModel?: any;
  filterFromLeafRows?: boolean;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  getSubRows?: (row: T) => T[];
  meta?: Record<string, any>;
  initialSorting?: SortingState;
  rowValidationSchema?: ZodType<any, ZodTypeDef, any>;
  keepExpandedRowsOnDepthLevel?: number[];
  columnFilters?: ColumnFilter[];
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  onColumnFilterChange?: (filterState: ColumnFilter[]) => void;
  manualFiltering?: boolean;
  manualPagination?: boolean;
  focusedRowUrlParamName?: string;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (value: Record<string, boolean>) => void;
  rowPinning?: RowPinningState;
  onRowPinningChange?: (value: RowPinningState) => void;
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  stickyColumns?: {
    left?: string[];
    right?: string[];
  };
  footer?: React.ReactNode | false;
  initialServerSideFilters?: ServerFilters<string>;
  serverSideFilters?: ServerFilters<string>;
  onServerSideFiltersClear?: () => void;
  globalServerSideSearch?: string;
}>;

export const TableInstanceProvider = <T,>({
  children,
  ...props
}: TableInstanceProviderProps<T>) => {
  const tableInstance = useTable<T>(props);
  const value = useMemo(() => tableInstance, [tableInstance]);

  return (
    <TableInstanceContext.Provider value={value}>
      {children}
    </TableInstanceContext.Provider>
  );
};

type TableProps<T = unknown> = Omit<
  BaseTableProps<T>,
  | "table"
  | "clickableColumns"
  | "columnVisibility"
  | "columns"
  | "data"
  | "stickyColumns"
  | "footer"
>;

function _Table<T = unknown>(
  props: TableProps<T>,
  ref: React.ForwardedRef<HTMLTableElement>,
) {
  const {
    table,
    clickableColumns,
    columnVisibility,
    columns,
    data,
    stickyColumns,
    footer,
  } = useTableInstanceContext<T>();

  return (
    <BaseTable<T>
      ref={ref}
      table={table}
      clickableColumns={clickableColumns}
      columnVisibility={columnVisibility}
      columns={columns}
      data={data}
      stickyColumns={stickyColumns}
      footer={footer}
      {...props}
    />
  );
}

export const Table = forwardRef(_Table) as <T = any>(
  props: TableProps<T> & { ref?: React.ForwardedRef<HTMLTableElement> },
) => React.ReactElement;

// Pagination component
type TablePaginationProps = {
  disablePagination: boolean;
};

export const TablePagination = ({
  disablePagination,
}: TablePaginationProps) => {
  const { table } = useTableInstanceContext();
  return (
    <DataTablePagination disablePagination={disablePagination} table={table} />
  );
};

type TableFiltersProps<T> = PropsWithChildren<{
  table: TableType<T>;
  columnFilters: ColumnFilter[];
}>;

export const TableFilters = <T,>({
  children,
  table,
  columnFilters,
}: TableFiltersProps<T>) => {
  const onSubmit = useCallback(
    (data: Record<string, unknown>) => {
      const currentFilters = table.getState().columnFilters;

      const filters = Object.entries(data).map(([key, value]) => ({
        id: key,
        value,
      }));

      const currentFilterMap = new Map(
        currentFilters.map((filter) => [filter.id, filter]),
      );

      filters.forEach((filter) => {
        currentFilterMap.set(filter.id, filter);
      });

      const mergedFilters = Array.from(currentFilterMap.values());

      table.setColumnFilters(mergedFilters);
    },
    [table],
  );

  const values = useMemo(() => {
    return columnFilters.reduce(
      (acc: Record<string, unknown>, filter: ColumnFilter) => {
        acc[filter.id] = filter.value;
        return acc;
      },
      {},
    );
  }, [columnFilters]);

  return (
    <FiltersRoot onSubmit={onSubmit} values={values}>
      {children}
    </FiltersRoot>
  );
};

export type InvoiceRowData = {
  invoiceId: string;
} & Omit<GetAllInvoiceInvoice, "invoiceDetailsId">;
